import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import { styled } from '@mui/material/styles'
import { useState } from 'react'

import { useAppBar } from '../../hooks/useAppBar'
import { PricingModal } from '../pricing'

import AppBarHeader from './AppBarHeader'
import NavigationDrawer from './NavigationDrawer'

const drawerWidth = 240

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open'
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen
    }),
    marginRight: drawerWidth
  })
}))

/**
 * PersistentDrawerRight - Main application bar with navigation drawer
 *
 * Container component that orchestrates the app bar header and navigation drawer.
 * Uses the useAppBar hook for state management and user actions.
 */
export default function PersistentDrawerRight() {
  const {
    open,
    session,
    handleDrawerOpen,
    handleDrawerClose,
    handleSignOut,
    toggleDarkMode
  } = useAppBar()

  const [pricingModalOpen, setPricingModalOpen] = useState(false)

  const handleOpenPricingModal = () => {
    setPricingModalOpen(true)
    handleDrawerClose()
  }

  const handleClosePricingModal = () => {
    setPricingModalOpen(false)
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position='fixed'
        open={open}
        elevation={3}
        sx={{
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? theme.palette.background.paper
              : theme.palette.primary.main,
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          zIndex: theme => theme.zIndex.drawer + 1 // Ensure AppBar is above other content
        }}
      >
        <AppBarHeader
          user={session.user}
          onDrawerOpen={handleDrawerOpen}
          drawerOpen={open}
        />
      </AppBar>

      <NavigationDrawer
        open={open}
        user={session.user}
        onClose={handleDrawerClose}
        onSignOut={handleSignOut}
        onToggleDarkMode={toggleDarkMode}
        onOpenPricingModal={handleOpenPricingModal}
      />

      <PricingModal open={pricingModalOpen} onClose={handleClosePricingModal} />
    </Box>
  )
}
